import { useState, useCallback } from 'react';

export interface HomeAgentInputState {
  value: string;
  isComposing: boolean;
  isSending: boolean;
}

/** 待发送技能元信息（AppNew 写入 store：pendingSkillId + pendingSkillName） */
export interface PendingSkillMeta {
  id: string;
  name: string;
}

export function useAgentInput(
  onSend: (value: string, skill?: PendingSkillMeta) => void,
  getSkill?: () => PendingSkillMeta | null | undefined,
  onSuccess?: () => void,
) {
  const [state, setState] = useState<HomeAgentInputState>({
    value: '',
    isComposing: false,
    isSending: false,
  });

  const setValue = useCallback((value: string) => {
    setState((prev) => ({ ...prev, value }));
  }, []);

  const setIsComposing = useCallback((isComposing: boolean) => {
    setState((prev) => ({ ...prev, isComposing }));
  }, []);

  const send = useCallback(async () => {
    const trimmed = state.value.trim();
    const skill = getSkill?.() ?? undefined;
    if ((!trimmed && !skill) || state.isSending) return;
    setState((prev) => ({ ...prev, isSending: true }));
    try {
      // 技能以 { id, name } 显式传递（由 AppNew 写入 pendingSkillId /
      // pendingSkillName），消息文本由 usePrepareAndSend 渲染为技能标签
      await onSend(trimmed, skill);
      setState((prev) => ({ ...prev, value: '', isSending: false }));
      onSuccess?.();
    } catch {
      setState((prev) => ({ ...prev, isSending: false }));
    }
  }, [state.value, state.isSending, onSend, getSkill, onSuccess]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !state.isComposing) {
        e.preventDefault();
        send();
      }
    },
    [send, state.isComposing]
  );

  return {
    ...state,
    setValue,
    setIsComposing,
    send,
    handleKeyDown,
  };
}
